"use client";

import React, { FC, useContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import ReactHtmlParser from "react-html-parser";

import useFetchMenuIdBasedProductList from "@hooks/search/use-fetch-menu-based-product-list";

import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useStoreContext } from "@components/store/provider";

import { getIsLockerShop } from "@selectors/store";

import ShadowCard from "@sharedComponents/cards/shadow-card";
import MultiCarousel from "@stadium/ui/src/components/multi-carousel";
import Multimedia from "@sharedComponents/multimedia";
import { ExternalButton } from "@sharedComponents/buttons";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { fetchCarouselProductsList } from "@graphql/queries/stores/products/fetch-carousel-product-list";
import { getMenuAllChildren } from "@selectors/menu";
import { getAllMenuTaxons } from "@helpers/menu";

import styles from "./main.module.scss";

interface Product {
  id: number;
  name: string;
  baseImage: string;
  description: string;
}

interface ProductCarouselProps {
  productCarouselMenu: { id: string; numberOfVisibleItems: number };
  data: {
    jsonContent: string;
    imageOne?: string;
    imageTwo?: string;
  };
}

const emptyObj = {};

const ProductCarousel: FC<ProductCarouselProps> = ({ productCarouselMenu, data }) => {
  const { countryCode } = useStoreContext();
  const { getStoreUpdatedAt } = usePreviewModeContext();
  const allChildren = useSelector(getMenuAllChildren);
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const isLockerShop = useSelector(getIsLockerShop);

  const { jsonContent, imageOne: desktopImg, imageTwo: mobileImg } = data || emptyObj;
  const {
    rte_content = "",
    button_text,
    button_link = "",
    title = "",
    subtitle = "",
    rte_bottom_content = "",
    bg_color = "",
  } = (jsonContent && JSON.parse(jsonContent)) || emptyObj;

  const topImg = isMobileDevice ? mobileImg : desktopImg;

  const { id: carouselMenuId = "", numberOfVisibleItems = 10 } = productCarouselMenu || emptyObj;

  const { allMenuTaxonIds: taxonIds, anyMenuWithSwag: withSwag } = useMemo(
    () => getAllMenuTaxons(allChildren),
    [allChildren, countryCode],
  );

  const variables = useMemo(
    () => ({
      menuId: +carouselMenuId,
      perPage: numberOfVisibleItems,
      storeUpdatedAt: getStoreUpdatedAt(),
      taxonIds,
      withSwag,
      optimizedSearch: isLockerShop,
    }),
    [carouselMenuId, numberOfVisibleItems, getStoreUpdatedAt, taxonIds, withSwag],
  );

  const { loading, fetchedProducts, getProductListing } = useFetchMenuIdBasedProductList({
    variables,
    gqlQuery: fetchCarouselProductsList,
  });

  useEffect(() => {
    if (carouselMenuId && taxonIds.length > 0) getProductListing();
  }, [carouselMenuId, variables, countryCode, taxonIds]);

  const renderShimmerLoader = () =>
    Array.from({ length: numberOfVisibleItems }).map((_, i) => (
      <div key={`card-${i}`} className={styles.colCardClass}>
        <ShadowCard />
      </div>
    ));

  const renderProductItem = ({ baseImage, name, description, id }: Product) => (
    <div key={id} className={styles.productCarouselCard}>
      <div className={styles.productCarouselCardImageHolder}>
        <Multimedia
          src={baseImage}
          alt={name}
          type="image"
          className={styles.productCarouselCardImage}
        />
      </div>
      <div className={styles.productCarouselCardContentHolder}>
        <h3 className={styles.productCarouselCardTitle}>{name}</h3>
        <div className={styles.productCarouselCardDescription}>{ReactHtmlParser(description)}</div>
      </div>
    </div>
  );

  if (isEmpty(productCarouselMenu) || (!loading && fetchedProducts?.length === 0)) return null;

  return (
    <article className={styles.productCarousel} style={{ backgroundColor: bg_color }}>
      <div className={styles.productCarouselBannerContainer}>
        {topImg && (
          <Multimedia type="image" src={topImg} className={styles.productCarouselBannerImage} />
        )}
      </div>
      <header className={styles.productCarouselHeader}>
        {title && <h2 className={styles.carouselHeaderTitle}>{title}</h2>}
        {subtitle && <div className={styles.carouselHeaderHeading}>{subtitle}</div>}
        {rte_content && (
          <div className={styles.carouselHeaderDescription}>{ReactHtmlParser(rte_content)}</div>
        )}
      </header>
      <main className={styles.productCarouselCardsContainer}>
        <MultiCarousel
          arrows
          transitionDuration={500}
          showDots={!loading}
          itemClass={styles.carouselItem}
          containerClass={styles.carouselContainer}
          responsive={{
            superLargeDesktop: {
              breakpoint: { max: 4000, min: 2000 },
              slidesToSlide: 1,
              items: 6,
              partialVisibilityGutter: 50,
            },
            desktop: {
              breakpoint: { max: 1999, min: 1024 },
              slidesToSlide: 1,
              items: 4,
              partialVisibilityGutter: 50,
            },
            tablet: {
              breakpoint: { max: 1023, min: 768 },
              slidesToSlide: 1,
              items: 2,
              partialVisibilityGutter: 50,
            },
            mobile: {
              breakpoint: { max: 767, min: 0 },
              slidesToSlide: 1,
              items: 1,
              partialVisibilityGutter: 5,
            },
          }}
          centerMode
        >
          {loading ? renderShimmerLoader() : fetchedProducts.map(renderProductItem)}
        </MultiCarousel>
      </main>
      <footer className={styles.productCarouselFooter}>
        {button_link && (
          <ExternalButton
            href={button_link}
            styleType="primary"
            className={styles.productCarouselFooterButton}
          >
            {button_text}
          </ExternalButton>
        )}
        {rte_bottom_content && (
          <div className={styles.productCarouselFooterMessage}>
            {ReactHtmlParser(rte_bottom_content)}
          </div>
        )}
      </footer>
    </article>
  );
};

export default ProductCarousel;
